import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMeetings } from '../../queries/meetings';
import { MeetingCard } from './MeetingCard';
import { ModelPerformancePanel } from './ModelPerformancePanel';
import { ExpectedWinnersPanel } from './ExpectedWinnersPanel';
import { filterMeetings, getMeetingReadiness, MeetingFilter, shiftDate, todayDateString } from './meetings-helpers';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard } from '../../components/ui/StatCard';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';

const FILTERS: { key: MeetingFilter; label: (total: number, ready: number) => string }[] = [
  { key: 'all', label: (total) => `All (${total})` },
  { key: 'ready', label: (_total, ready) => `Ready (${ready})` },
  { key: 'missing', label: (total, ready) => `Missing (${total - ready})` },
];

const DATE_KEY = 'meetings:date'; // remember the working date (often 1-2 days ahead)

export function MeetingsListPage() {
  // Restore the last-worked date so returning to this page keeps it (staff work
  // days ahead); fall back to today.
  const [date, setDate] = useState(() => {
    try {
      return localStorage.getItem(DATE_KEY) || todayDateString();
    } catch {
      return todayDateString();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(DATE_KEY, date);
    } catch {
      /* ignore */
    }
  }, [date]);
  const [filter, setFilter] = useState<MeetingFilter>('all');
  const { data, isLoading, error } = useMeetings(date);

  const meetings = data ?? [];
  const readyCount = meetings.filter((m) => getMeetingReadiness(m).ready).length;
  const filtered = filterMeetings(meetings, filter);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Race Meetings"
        description="Ingestion readiness and analysis coverage for the selected date."
        actions={
          <div className="inline-flex items-center rounded-md border border-input bg-card shadow-sm">
            <button
              type="button"
              onClick={() => setDate(shiftDate(date, -1))}
              aria-label="Previous day"
              className="flex h-9 w-9 items-center justify-center rounded-l-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input
              type="date"
              aria-label="Meeting date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 border-x border-input bg-transparent px-3 text-sm focus-visible:outline-none"
            />
            <button
              type="button"
              onClick={() => setDate(shiftDate(date, 1))}
              aria-label="Next day"
              className="flex h-9 w-9 items-center justify-center rounded-r-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <StatCard label="Total Meetings" value={meetings.length} />
        <StatCard label="Ready to Analyse" value={readyCount} />
        <StatCard label="Pending Data" value={meetings.length - readyCount} />
      </div>

      <ExpectedWinnersPanel date={date} />

      <ModelPerformancePanel date={date} />

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              filter === f.key
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'border border-border text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {f.label(meetings.length, readyCount)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Spinner label="Loading meetings…" />
      ) : error ? (
        <div className="py-8 text-center text-destructive">
          {error instanceof Error ? error.message : 'Failed to load meetings.'}
        </div>
      ) : meetings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">
          No meetings found for this date.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">
          No meetings match the selected filter.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <MeetingCard key={m.meeting_id} meeting={m} />
          ))}
        </div>
      )}
    </div>
  );
}
